import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Calendar as CalendarIcon, Plus, XCircle, BarChart3, Download, ExternalLink, Home, Trash2, Edit, X } from 'lucide-react';
import { activityService, Activity } from '../services/activityService';
import GanttChart from './GanttChart';
import { useNavigate } from 'react-router-dom';
import { designTokens } from '../design/designTokens';
import { useUser } from '../contexts/UserContext';

const Container = styled.div`
  max-width: 1400px; 
  margin: 0 auto; 
  padding: ${designTokens.spacing[6]}; 
  font-family: ${designTokens.typography.fontFamily.primary};
  background: ${designTokens.colors.background.primary};
  min-height: 100vh;
  color: ${designTokens.colors.text.primary};

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]};
  }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${designTokens.spacing[6]};
  margin-top: ${designTokens.spacing[6]};
  align-items: start;

  ${designTokens.mediaQueries.maxLg} {
    grid-template-columns: 1fr;
    gap: ${designTokens.spacing[4]};
  }
`;

const Header = styled.div`
  background: ${designTokens.colors.background.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[6]};
  margin-bottom: ${designTokens.spacing[8]};
  box-shadow: ${designTokens.shadows.card};
  border: 1px solid ${designTokens.colors.border.primary};

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]};
    margin-bottom: ${designTokens.mobileSpacing[6]};
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: ${designTokens.typography.fontSize['2xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.riverPoultry.brandColors.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[3]};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xl};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AddButton = styled.button`
  background: ${designTokens.riverPoultry.gradients.primary};
  color: ${designTokens.colors.text.inverse}; 
  border: none; 
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[8]}; 
  border-radius: ${designTokens.borderRadius.full}; 
  font-weight: ${designTokens.typography.fontWeight.semibold}; 
  cursor: pointer;
  display: flex; 
  align-items: center; 
  gap: ${designTokens.spacing[2]}; 
  transition: ${designTokens.transitions.default};
  box-shadow: ${designTokens.shadows.button};
  font-size: ${designTokens.typography.fontSize.base};
  
  &:hover { 
    transform: translateY(-2px); 
    box-shadow: ${designTokens.shadows.buttonHover};
  }
  
  &:active { 
    transform: translateY(0); 
  }

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]} ${designTokens.mobileSpacing[6]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;


const ExportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const ExportTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExportButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const CalendarSection = styled.div`
  background: ${designTokens.colors.surface.primary}; 
  border-radius: ${designTokens.borderRadius['2xl']}; 
  padding: ${designTokens.spacing[4]}; 
  box-shadow: ${designTokens.shadows.lg};
  border: 1px solid ${designTokens.colors.border.light};
  transition: ${designTokens.transitions.default};
  backdrop-filter: blur(10px);
  height: fit-content;
  
  &:hover {
    box-shadow: ${designTokens.shadows.xl};
    transform: translateY(-2px);
  }
`;

const TodaysActivitiesSection = styled.div`
  background: ${designTokens.colors.surface.primary}; 
  border-radius: ${designTokens.borderRadius['2xl']}; 
  padding: ${designTokens.spacing[4]}; 
  box-shadow: ${designTokens.shadows.lg};
  border: 1px solid ${designTokens.colors.border.light};
  transition: ${designTokens.transitions.default};
  backdrop-filter: blur(10px);
  height: fit-content;
  
  &:hover {
    box-shadow: ${designTokens.shadows.xl};
    transform: translateY(-2px);
  }
`;

const ActivitiesSection = styled.div`
  background: ${designTokens.colors.surface.primary}; 
  border-radius: ${designTokens.borderRadius.xl}; 
  padding: ${designTokens.spacing[6]}; 
  box-shadow: ${designTokens.shadows.card};
  border: 1px solid ${designTokens.colors.border.light};
  transition: ${designTokens.transitions.default};
  grid-column: 1 / -1;
  margin-top: ${designTokens.spacing[6]};
  
  &:hover {
    box-shadow: ${designTokens.shadows.cardHover};
  }
`;

const ViewToggleGroup = styled.div`
  display: flex;
  background: ${designTokens.colors.surface.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[1]};
  border: 1px solid ${designTokens.colors.border.primary};
  margin-bottom: ${designTokens.spacing[5]};
  width: fit-content;
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  padding: ${designTokens.spacing[3]} ${designTokens.spacing[6]};
  border: none;
  border-radius: ${designTokens.borderRadius.lg};
  font-weight: ${designTokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: ${designTokens.transitions.fast};
  font-size: ${designTokens.typography.fontSize.sm};
  
  ${props => props.active ? `
    background: ${designTokens.riverPoultry.gradients.primary};
    color: white;
    box-shadow: ${designTokens.shadows.button};
  ` : `
    background: transparent;
    color: ${designTokens.colors.text.secondary};
    
    &:hover {
      background: ${designTokens.colors.surface.interactive};
      color: ${designTokens.colors.text.brand};
    }
  `}
`;

const SectionTitle = styled.h2`
  font-size: ${designTokens.typography.fontSize.xl}; 
  font-weight: ${designTokens.typography.fontWeight.bold}; 
  color: ${designTokens.riverPoultry.brandColors.primary}; 
  margin: 0 0 ${designTokens.spacing[6]} 0; 
  display: flex; 
  align-items: center; 
  gap: ${designTokens.spacing[3]};
  padding-bottom: ${designTokens.spacing[4]};
  border-bottom: 3px solid ${designTokens.riverPoultry.brandColors.primaryLight};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${designTokens.riverPoultry.gradients.primary};
    border-radius: 2px;
  }
`;

const CalendarView = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  background: ${designTokens.colors.surface.primary};
  border-radius: ${designTokens.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${designTokens.shadows.lg};
  border: 1px solid ${designTokens.colors.border.light};
  min-height: 320px;
`;

const DayHeader = styled.div`
  background: ${designTokens.colors.surface.secondary};
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[2]};
  text-align: center;
  font-weight: ${designTokens.typography.fontWeight.bold};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.riverPoultry.brandColors.primary};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid ${designTokens.colors.border.light};
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 20%;
    height: 60%;
    width: 1px;
    background: ${designTokens.colors.border.light};
  }
`;

const DayCell = styled.div<{ hasEvent: boolean; isToday: boolean; isCurrentMonth: boolean }>`
  background: ${props => 
    props.isCurrentMonth ? designTokens.colors.surface.primary : 
    designTokens.colors.surface.secondary
  };
  padding: ${designTokens.spacing[2]};
  min-height: 70px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid ${designTokens.colors.border.light};
  border-bottom: 1px solid ${designTokens.colors.border.light};

  &:nth-child(7n) {
    border-right: none;
  }

  &:hover {
    background: ${designTokens.colors.surface.interactive};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.15);
    z-index: 2;
  }

  ${designTokens.mediaQueries.maxMd} {
    min-height: 60px;
    padding: ${designTokens.mobileSpacing[1]};
  }
`;

const DayNumber = styled.div<{ isCurrentMonth: boolean; isToday: boolean }>`
  font-weight: ${props => props.isToday ? designTokens.typography.fontWeight.bold : designTokens.typography.fontWeight.semibold};
  color: ${props => 
    props.isToday ? designTokens.colors.text.inverse :
    props.isCurrentMonth ? designTokens.colors.text.primary : designTokens.colors.text.muted
  };
  font-size: ${designTokens.typography.fontSize.base};
  margin-bottom: ${designTokens.spacing[2]};
  align-self: flex-start;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  border-radius: 50%;
  background: ${props => props.isToday ? designTokens.riverPoultry.gradients.primary : 'transparent'};
  position: relative;
  z-index: 1;

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.sm};
    width: 24px;
    height: 24px;
  }
`;


const CalendarEventTitle = styled.div<{ type: 'chick' | 'vaccination' | 'feeding' | 'cleaning' | 'maintenance' | 'harvest' | 'other' }>`
  background: ${props => {
    switch (props.type) {
      case 'chick': return designTokens.riverPoultry.brandColors.secondary;
      case 'vaccination': return designTokens.colors.status.error;
      case 'feeding': return designTokens.riverPoultry.brandColors.primary;
      case 'cleaning': return designTokens.colors.status.info;
      case 'maintenance': return designTokens.colors.status.warning;
      case 'harvest': return designTokens.colors.status.success;
      default: return designTokens.riverPoultry.brandColors.primary;
    }
  }};
  color: white;
  font-size: ${designTokens.typography.fontSize.xs};
  font-weight: ${designTokens.typography.fontWeight.medium};
  padding: ${designTokens.spacing[1]} ${designTokens.spacing[2]};
  border-radius: ${designTokens.borderRadius.sm};
  margin: ${designTokens.spacing[1]} 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.3;
  min-height: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border-left: 3px solid rgba(255, 255, 255, 0.3);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 3;
  }
`;

const EventTitle = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventActions = styled.div`
  display: none;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  
  ${CalendarEventTitle}:hover & {
    display: flex;
  }
`;

const EventActionButton = styled.button<{ variant: 'edit' | 'delete' }>`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 2px;
  padding: 1px 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
  
  &:hover {
    background: ${props => props.variant === 'delete' ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.4)'};
    transform: scale(1.1);
  }
`;


const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${designTokens.colors.overlay.dark};
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${designTokens.colors.background.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[8]};
  width: 90%;
  max-width: 550px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: ${designTokens.shadows.modal};
  border: 1px solid ${designTokens.colors.border.primary};

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[6]};
    width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${designTokens.typography.fontSize.xl};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.primary};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.lg};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: #666;

  &:hover {
    background: #f0f0f0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${designTokens.spacing[2]};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.primary};
  font-size: ${designTokens.typography.fontSize.sm};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[4]};
  border: 2px solid ${designTokens.colors.border.primary};
  border-radius: ${designTokens.borderRadius.md};
  font-size: ${designTokens.typography.fontSize.base};
  transition: ${designTokens.transitions.default};
  background: ${designTokens.colors.background.tertiary};
  font-family: ${designTokens.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${designTokens.riverPoultry.brandColors.primary};
    background: ${designTokens.colors.background.secondary};
    box-shadow: 0 0 0 3px ${designTokens.riverPoultry.brandColors.primaryLight};
  }

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[4]};
  border: 2px solid ${designTokens.colors.border.primary};
  border-radius: ${designTokens.borderRadius.md};
  font-size: ${designTokens.typography.fontSize.base};
  background: ${designTokens.colors.background.tertiary};
  cursor: pointer;
  transition: ${designTokens.transitions.default};
  font-family: ${designTokens.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${designTokens.riverPoultry.brandColors.primary};
    background: ${designTokens.colors.background.secondary};
    box-shadow: 0 0 0 3px ${designTokens.riverPoultry.brandColors.primaryLight};
  }

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[4]};
  border: 2px solid ${designTokens.colors.border.primary};
  border-radius: ${designTokens.borderRadius.md};
  font-size: ${designTokens.typography.fontSize.base};
  min-height: 100px;
  resize: vertical;
  background: ${designTokens.colors.background.tertiary};
  transition: ${designTokens.transitions.default};
  font-family: ${designTokens.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${designTokens.riverPoultry.brandColors.primary};
    background: ${designTokens.colors.background.secondary};
    box-shadow: 0 0 0 3px ${designTokens.riverPoultry.brandColors.primaryLight};
  }

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[7]};
  border: none;
  border-radius: ${designTokens.borderRadius.md};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${designTokens.transitions.default};
  font-size: ${designTokens.typography.fontSize.base};
  font-family: ${designTokens.typography.fontFamily.primary};

  ${props => props.variant === 'primary' ? `
    background: ${designTokens.riverPoultry.gradients.primary};
    color: ${designTokens.colors.text.inverse};
    box-shadow: ${designTokens.shadows.button};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${designTokens.shadows.buttonHover};
    }
    
    &:active {
      transform: translateY(0);
    }
  ` : `
    background: ${designTokens.colors.background.tertiary};
    color: ${designTokens.colors.text.muted};
    border: 2px solid ${designTokens.colors.border.primary};
    
    &:hover {
      background: ${designTokens.colors.background.hover};
      color: ${designTokens.colors.text.primary};
      border-color: ${designTokens.colors.border.secondary};
    }
  `}

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]} ${designTokens.mobileSpacing[6]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const ExportButton = styled.button`
  padding: 12px 24px;
  border-radius: 25px;
  border: 1px solid #dee2e6;
  background: white;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f8f9fa;
    border-color: #2e7d32;
    color: #2e7d32;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(46, 125, 50, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ClearButton = styled.button`
  padding: 12px 24px;
  border-radius: 25px;
  border: 1px solid #dc3545;
  background: white;
  color: #dc3545;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #dc3545;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.25);
  }
  
  &:active {
    transform: translateY(0);
  }
`;



const Calendar: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { canDeleteAll } = useUser();
  const [currentDate] = useState(new Date());
  const [, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Activity | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [ganttViewMode, setGanttViewMode] = useState<'week' | 'month' | 'quarter'>('week');
  const [formData, setFormData] = useState({
    title: '', type: 'other' as Activity['type'], date: '', time: '', location: '', description: '',
    assignedTo: '', priority: 'medium' as Activity['priority'], estimatedDuration: 1
  });

  useEffect(() => setActivities(activityService.loadActivities()), []);
  useEffect(() => {
    const refresh = () => !document.hidden && setActivities(activityService.loadActivities());
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, []);

  const getDaysInMonth = useCallback((date: Date) => {
    const year = date.getFullYear(), month = date.getMonth();
    const firstDay = new Date(year, month, 1), lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate(), startingDayOfWeek = firstDay.getDay();
    return [...Array(startingDayOfWeek).fill(null), ...Array.from({length: daysInMonth}, (_, i) => new Date(year, month, i + 1))];
  }, []);

  const getEventsForDate = useCallback((date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return activities.filter(activity => 
      (activity.startDate || activity.scheduledDate) <= dateStr && 
      (activity.endDate || activity.scheduledDate) >= dateStr
    );
  }, [activities]);

  const generateICalData = (activities: Activity[]): string => {
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ical = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Farm Management//Farm Activities//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH'];
    
    activities.forEach(activity => {
      const startDate = new Date(activity.startDate + (activity.startTime ? `T${activity.startTime}` : ''));
      const endDate = new Date(activity.endDate + (activity.endTime ? `T${activity.endTime}` : ''));
      if (!activity.endTime) endDate.setHours(startDate.getHours() + 1);
      const startStr = startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      const endStr = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      ical.push('BEGIN:VEVENT', `UID:${activity.id}@farm-management.local`, `DTSTAMP:${timestamp}`, `DTSTART:${startStr}`, `DTEND:${endStr}`, `SUMMARY:${activity.title}`, `DESCRIPTION:${activity.description || ''}\\nLocation: ${activity.location}\\nAssigned to: ${activity.assignedTo}\\nPriority: ${activity.priority}`, `LOCATION:${activity.location}`, `STATUS:${activity.status.toUpperCase()}`, 'END:VEVENT');
    });
    
    return [...ical, 'END:VCALENDAR'].join('\r\n');
  };

  const exportToICal = () => {
    const icalData = generateICalData(activities);
    const blob = new Blob([icalData], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'farm-activities.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToGoogleCalendar = () => {
    const googleCalendarUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
    const activitiesParam = activities.map(activity => {
      const startDate = new Date(activity.startDate + (activity.startTime ? `T${activity.startTime}` : ''));
      const endDate = new Date(activity.endDate + (activity.endTime ? `T${activity.endTime}` : ''));
      
      if (!activity.endTime) {
        endDate.setHours(startDate.getHours() + 1);
      }

      const startStr = startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      const endStr = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

      return {
        text: activity.title,
        dates: `${startStr}/${endStr}`,
        details: `${activity.description || ''}\nLocation: ${activity.location}\nAssigned to: ${activity.assignedTo}\nPriority: ${activity.priority}`,
        location: activity.location
      };
    });

    // For multiple events, we'll export the first one and show a message
    if (activitiesParam.length > 0) {
      const firstActivity = activitiesParam[0];
      const url = `${googleCalendarUrl}&text=${encodeURIComponent(firstActivity.text)}&dates=${firstActivity.dates}&details=${encodeURIComponent(firstActivity.details)}&location=${encodeURIComponent(firstActivity.location)}`;
      window.open(url, '_blank');
      
      if (activitiesParam.length > 1) {
        // Opening Google Calendar
      }
    } else {
      // No activities to export
    }
  };

  const exportToZohoCalendar = () => {
    const zohoCalendarUrl = 'https://calendar.zoho.com/calendar/event';
    const activitiesParam = activities.map(activity => {
      const startDate = new Date(activity.startDate + (activity.startTime ? `T${activity.startTime}` : ''));
      const endDate = new Date(activity.endDate + (activity.endTime ? `T${activity.endTime}` : ''));
      
      if (!activity.endTime) {
        endDate.setHours(startDate.getHours() + 1);
      }

      return {
        title: activity.title,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        description: `${activity.description || ''}\nLocation: ${activity.location}\nAssigned to: ${activity.assignedTo}\nPriority: ${activity.priority}`,
        location: activity.location
      };
    });

    // For multiple events, we'll export the first one and show a message
    if (activitiesParam.length > 0) {
      const firstActivity = activitiesParam[0];
      const url = `${zohoCalendarUrl}?title=${encodeURIComponent(firstActivity.title)}&start=${encodeURIComponent(firstActivity.start)}&end=${encodeURIComponent(firstActivity.end)}&description=${encodeURIComponent(firstActivity.description)}&location=${encodeURIComponent(firstActivity.location)}`;
      window.open(url, '_blank');
      
      if (activitiesParam.length > 1) {
        // Opening Zoho Calendar
      }
    } else {
      // No activities to export
    }
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      type: 'other',
      date: '',
      time: '',
      location: '',
      description: '',
      assignedTo: '',
      priority: 'medium',
      estimatedDuration: 1
    });
    setIsModalOpen(true);
  };

  const handleEditEvent = (event: Activity) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      type: event.type,
      date: event.startDate || event.scheduledDate,
      time: event.startTime || '',
      location: event.location,
      description: event.description,
      assignedTo: event.assignedTo || '',
      priority: event.priority,
      estimatedDuration: event.estimatedDuration
    });
    setIsModalOpen(true);
  };

  const handleSaveEvent = () => {
    if (editingEvent) {
      // Update existing event
      activityService.updateActivity(editingEvent.id, {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        startDate: formData.date,
        endDate: formData.date,
        startTime: formData.time,
        endTime: formData.time,
        location: formData.location,
        assignedTo: formData.assignedTo,
        priority: formData.priority,
        estimatedDuration: formData.estimatedDuration
      });
    } else {
      // Add new event
      activityService.addActivity({
        title: formData.title,
        description: formData.description,
        type: formData.type,
        status: 'pending',
        priority: formData.priority,
        scheduledDate: formData.date,
        startDate: formData.date,
        endDate: formData.date,
        startTime: formData.time,
        endTime: formData.time,
        location: formData.location,
        assignedTo: formData.assignedTo,
        estimatedDuration: formData.estimatedDuration
      });
    }
    
    // Refresh activities
    const loadedActivities = activityService.loadActivities();
    setActivities(loadedActivities);
    setIsModalOpen(false);
  };

  const handleClearAllActivities = () => {
    if (window.confirm('Are you sure you want to clear all calendar activities and Gantt chart data? This action cannot be undone.')) {
      // Clear all activities from the service
      activityService.clearAllActivities();
      // Update the state to reflect the empty data
      setActivities([]);
    }
  };

  const handleDeleteActivity = useCallback((activityId: string, activityTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${activityTitle}"? This action cannot be undone.`)) {
      // Delete the activity using the service
      const success = activityService.deleteActivity(activityId);
      if (success) {
        // Refresh activities
        const loadedActivities = activityService.loadActivities();
        setActivities(loadedActivities);
      }
    }
  }, []);

  const handleEditActivityClick = useCallback((activity: Activity, event: React.MouseEvent) => {
    event.stopPropagation();
    handleEditEvent(activity);
  }, []);


  const days = useMemo(() => getDaysInMonth(currentDate), [currentDate, getDaysInMonth]);
  const today = useMemo(() => new Date(), []);
  const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Container>
      <Header>
        <HeaderTop>
          <Title>
            <CalendarIcon size={32} />
            Activity Plans
          </Title>
          <HeaderActions>
            <AddButton onClick={handleAddEvent}>
              <Plus size={20} />
              Add Activity
            </AddButton>
          </HeaderActions>
        </HeaderTop>
        
        <ExportSection>
          <ExportTitle>
            <Download size={20} />
            Export Options
          </ExportTitle>
          <ExportButtonGroup>
            <ExportButton onClick={exportToICal}>
              <Download size={16} />
              Export iCal
            </ExportButton>
            <ExportButton onClick={exportToGoogleCalendar}>
              <ExternalLink size={16} />
              Google Calendar
            </ExportButton>
            <ExportButton onClick={exportToZohoCalendar}>
              <ExternalLink size={16} />
              Zoho Calendar
            </ExportButton>
            {canDeleteAll && (
              <ClearButton onClick={handleClearAllActivities}>
                <Trash2 size={16} />
                Clear All Data
              </ClearButton>
            )}
          </ExportButtonGroup>
        </ExportSection>
      </Header>

      <MainLayout>
        <CalendarSection>
          <SectionTitle>
            <CalendarIcon size={24} />
            Calendar View
          </SectionTitle>
          <CalendarView>
            {dayNames.map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {days.map((day, index) => {
              if (!day) {
                return <DayCell key={index} hasEvent={false} isToday={false} isCurrentMonth={false} />;
              }
              
              const events = getEventsForDate(day);
              const isToday = day.toDateString() === today.toDateString();
              const isCurrentMonth = day.getMonth() === currentMonth;
              
              return (
                <DayCell 
                  key={index} 
                  hasEvent={events.length > 0} 
                  isToday={isToday}
                  isCurrentMonth={isCurrentMonth}
                  onClick={() => {
                    setSelectedDate(day);
                    // Pre-fill the form with the selected date
                    setFormData({
                      ...formData,
                      date: day.toISOString().split('T')[0]
                    });
                    setIsModalOpen(true);
                  }}
                  onDoubleClick={() => {
                    // Navigate to Dashboard with selected date
                    const dateStr = day.toISOString().split('T')[0];
                    navigate(`/?selectedDate=${dateStr}`);
                  }}
                >
                  <DayNumber isCurrentMonth={isCurrentMonth} isToday={isToday}>
                    {day.getDate()}
                  </DayNumber>
                {events.slice(0, 5).map((event, eventIndex) => (
                  <CalendarEventTitle 
                    key={eventIndex} 
                    type={event.type}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Only open edit if not clicking on action buttons
                      if (!(e.target as HTMLElement).closest('button')) {
                        handleEditEvent(event);
                      }
                    }}
                  >
                    <EventTitle>{event.title}</EventTitle>
                    <EventActions>
                      <EventActionButton 
                        variant="edit"
                        onClick={(e) => handleEditActivityClick(event, e)}
                        title="Edit activity"
                      >
                        <Edit size={10} />
                      </EventActionButton>
                      <EventActionButton 
                        variant="delete"
                        onClick={(e) => handleDeleteActivity(event.id, event.title, e)}
                        title="Delete activity"
                      >
                        <X size={10} />
                      </EventActionButton>
                    </EventActions>
                  </CalendarEventTitle>
                ))}
                {events.length > 5 && (
                  <CalendarEventTitle type="other">
                    +{events.length - 5} more
                  </CalendarEventTitle>
                )}
                </DayCell>
              );
            })}
          </CalendarView>
        </CalendarSection>

        <TodaysActivitiesSection>
          <SectionTitle>
            <BarChart3 size={24} />
            Today's Activities
          </SectionTitle>
          {getEventsForDate(today).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {getEventsForDate(today).map((event, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    background: event.type === 'chick' ? designTokens.riverPoultry.brandColors.secondary :
                               event.type === 'vaccination' ? designTokens.colors.status.error :
                               event.type === 'feeding' ? designTokens.riverPoultry.brandColors.primary :
                               event.type === 'cleaning' ? designTokens.colors.status.info :
                               event.type === 'maintenance' ? designTokens.colors.status.warning :
                               event.type === 'harvest' ? designTokens.colors.status.success :
                               designTokens.riverPoultry.brandColors.primary,
                    color: 'white',
                    borderRadius: designTokens.borderRadius.md,
                    fontSize: designTokens.typography.fontSize.sm,
                    fontWeight: designTokens.typography.fontWeight.medium,
                    boxShadow: designTokens.shadows.sm,
                    cursor: 'pointer',
                    transition: designTokens.transitions.fast
                  }}
                  onClick={() => handleEditEvent(event)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = designTokens.shadows.md;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = designTokens.shadows.sm;
                  }}
                >
                  <div style={{ fontWeight: designTokens.typography.fontWeight.semibold, marginBottom: '4px' }}>
                    {event.title}
                  </div>
                  <div style={{ fontSize: designTokens.typography.fontSize.xs, opacity: 0.9 }}>
                    {event.startTime || 'All day'} • {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </div>
                  {event.description && (
                    <div style={{ fontSize: designTokens.typography.fontSize.xs, opacity: 0.8, marginTop: '4px' }}>
                      {event.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: designTokens.colors.text.muted,
              fontSize: designTokens.typography.fontSize.sm
            }}>
              <CalendarIcon size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div>No activities scheduled for today</div>
              <div style={{ fontSize: designTokens.typography.fontSize.xs, marginTop: '8px' }}>
                Click "Add Activity" to schedule something
              </div>
            </div>
          )}
        </TodaysActivitiesSection>

        <ActivitiesSection>
          <SectionTitle>
            <BarChart3 size={24} />
            Activity Timeline
          </SectionTitle>
        
        <ViewToggleGroup>
          <ViewToggleButton 
            active={ganttViewMode === 'week'} 
            onClick={() => setGanttViewMode('week')}
          >
            Week
          </ViewToggleButton>
          <ViewToggleButton 
            active={ganttViewMode === 'month'} 
            onClick={() => setGanttViewMode('month')}
          >
            Month
          </ViewToggleButton>
          <ViewToggleButton 
            active={ganttViewMode === 'quarter'} 
            onClick={() => setGanttViewMode('quarter')}
          >
            Quarter
          </ViewToggleButton>
        </ViewToggleGroup>
        
        <GanttChart 
          activities={activities} 
          viewMode={ganttViewMode}
          onActivityClick={(activity) => {
            setEditingEvent(activity);
            setFormData({
              title: activity.title,
              type: activity.type,
              date: activity.startDate || activity.scheduledDate,
              time: activity.startTime || '',
              location: activity.location,
              description: activity.description,
              assignedTo: activity.assignedTo || '',
              priority: activity.priority,
              estimatedDuration: activity.estimatedDuration
            });
            setIsModalOpen(true);
          }}
        />
        </ActivitiesSection>
      </MainLayout>

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingEvent ? 'Edit Activity' : 'Add New Activity'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              <XCircle size={24} />
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>Activity Title</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Chick Arrival - Batch A"
            />
          </FormGroup>

          <FormGroup>
            <Label>Activity Type</Label>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'chick' | 'vaccination' | 'other' })}
            >
              <option value="chick">Chick Arrival</option>
              <option value="vaccination">Vaccination</option>
              <option value="other">Other Activity</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Date</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Time</Label>
            <Input
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Location</Label>
            <Input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Hatchery Building"
            />
          </FormGroup>

          <FormGroup>
            <Label>Assigned To</Label>
            <Input
              type="text"
              value={formData.assignedTo}
              onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
              placeholder="e.g., Farm Manager, John Doe"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional details about the activity..."
            />
          </FormGroup>

          <ButtonGroup>
            <Button onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            {!editingEvent && (
              <Button 
                onClick={() => {
                  const dateStr = formData.date;
                  if (dateStr) {
                    navigate(`/?selectedDate=${dateStr}`);
                    setIsModalOpen(false);
                  }
                }}
                style={{ backgroundColor: '#2196f3', color: 'white' }}
              >
                <Home size={16} />
                View Day's Activities
              </Button>
            )}
            <Button variant="primary" onClick={handleSaveEvent}>
              {editingEvent ? 'Update' : 'Add'} Activity
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  );
});

Calendar.displayName = 'Calendar';

export default Calendar;
